/**
 * CatalogueProductPicker — the catalogue preset of the one ProductPicker (refonte WP-37 /
 * cross-module XWP-12; ux/04 §3.2) for the screens that pick a product of the catalogue outside the
 * counters and the achat entry: achat urgent, appel d'offres, manquants. `GET /produits?search=`
 * (useCatalogueProductSearch) — matched and ranked by the server engine (A-11); the foundations
 * Combobox owns the honest states (« Recherche… », late answers dropped, « Réessayer ») and the keys
 * (↑ ↓ then Entrée; one result: Entrée picks it; Entrée in flight applied to the answer).
 * It replaces the hand-written `ProductSearchCombobox` of components/domain/Domain.tsx (which now
 * re-exports this preset under its old name) and its store helper that turned a failed search into
 * « no result ».
 *
 * Two modes (refonte WP-112, AUD-CP-17): by default a search field that empties itself after each pick
 * (the screens that add one line per pick); CONTROLLED when `value` is given — the field shows the
 * picked product (`selectedLabel`) until the caller lets it go (the bon de commande entry keeps it in
 * view until the line is added), a search typed over it and abandoned puts it back, and clearing the
 * field (Échap twice) calls `onClear`.
 */
import * as React from "react";
import { Combobox } from "@/components/ui/Combobox";
import { SearchResultRow } from "@/components/produit/SearchResultRow.jsx";
import { useCatalogueProductSearch } from "@/hooks/useProductSearch.js";
import { RECHERCHE_PRODUIT_COPIE as C } from "@/config/recherche-produit.messages.js";

/**
 * @param {object} props
 * @param {(produit: object) => void} props.onSelect  the chosen catalogue row (`GET /produits` shape)
 * @param {boolean} [props.autoFocus]
 * @param {string} [props.placeholder]
 * @param {string} [props.label]
 * @param {boolean} [props.disabled]
 * @param {string} [props.id]
 * @param {(query: string) => void} [props.onCreate]  « Créer la fiche « … » » — offered only once the
 *   server answered with no product (Alt+Entrée), never while a search runs or on a listed product
 * @param {(query: string) => string} [props.createLabel]
 * @param {(query: string) => void} [props.onQueryChange]
 * @param {string|null} [props.value]  controlled mode: the picked product's id (null = none picked yet)
 * @param {string} [props.selectedLabel]  controlled mode: the text shown for it
 * @param {() => void} [props.onClear]  controlled mode: the field was cleared
 */
export const CatalogueProductPicker = React.forwardRef(function CatalogueProductPicker(
  { onSelect, autoFocus, placeholder = C.placeholderPos, label = C.label, disabled, id, onCreate, createLabel, onQueryChange, value, selectedLabel, onClear },
  ref,
) {
  const loadOptions = useCatalogueProductSearch();
  const controle = value !== undefined;
  return (
    <Combobox
      ref={ref}
      id={id}
      size="md"
      label={label}
      placeholder={placeholder}
      loadOptions={loadOptions}
      value={controle ? value : null}
      selectedLabel={controle ? selectedLabel ?? "" : ""}
      resetOnPick={!controle}
      keepSelectionOnBlur={controle}
      queueEnter
      scanAware
      autoFocus={autoFocus}
      disabled={disabled}
      onChange={(o) => (o ? onSelect?.(o.produit) : controle && onClear?.())}
      onCreate={onCreate}
      createLabel={createLabel}
      createOnlyWhenEmpty
      onQueryChange={onQueryChange}
      emptyText={C.aucunCatalogue}
      emptyHint={C.aideMatching}
      noPickHint={C.aChoisir}
      errorText={C.echec}
      renderOption={(o, s) => <SearchResultRow option={o} query={s.query} active={s.active} />}
    />
  );
});

export default CatalogueProductPicker;
